import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarAlt,
    faTag,
    faChartLine,
    faDatabase,
    faUsers,
    faClock,
    faUser
} from '@fortawesome/free-solid-svg-icons';

interface BlogPost {
    id: string;
    title: string;
    summary: string;
    excerpt: string;
    date: string;
    author: string;
    readTime: string;
    tags: string[];
}

// Map of tag names to their corresponding icons
const tagIcons: { [key: string]: any } = {
    'Data': faDatabase,
    'Trends': faChartLine,
    'Industries': faChartLine,
    'Audience Size': faUsers,
    'default': faTag
};

// Every post here is pulled from the live SponsorDB database, not written
// as generic advice — see BlogPost.tsx for the full text (kept in sync
// with these summaries/tags).
const posts: BlogPost[] = [
    {
        id: '1',
        title: "What We've Tracked in Newsletter Sponsorships So Far",
        summary: "A look at every real sponsor-newsletter placement in the database — who's spending, on what kind of newsletter, and what the data does (and doesn't) tell us yet.",
        excerpt: "91 real sponsor placements, 86 companies, 37 newsletters. Here's what that data actually shows about who's buying newsletter placements right now — and one honest caveat about what it doesn't show yet.",
        date: 'August 29, 2026',
        author: 'SponsorDB Team',
        readTime: '3 min read',
        tags: ['Data', 'Trends']
    },
    {
        id: '2',
        title: 'Which Industries Are Actually Paying to Sponsor Newsletters',
        summary: "A ranked breakdown of the categories with real, current sponsor demand — and what it means if your newsletter isn't in one of them yet.",
        excerpt: 'Software, AI, and Business lead by a wide margin. Here\'s the full ranking, and what it means for your pitch list whether or not your niche made the top five.',
        date: 'August 30, 2026',
        author: 'SponsorDB Team',
        readTime: '3 min read',
        tags: ['Data', 'Industries']
    },
    {
        id: '3',
        title: 'How Big Does Your Newsletter Need to Be to Land a Sponsor?',
        summary: "We pulled the actual audience size of every newsletter placement in the database instead of guessing. Here's the real breakdown, and why it's not the whole story.",
        excerpt: "0% under 10K, 57% over 250K. The real numbers look discouraging at first — until you understand why our tracking is biased toward bigger newsletters, and what that means if you're smaller.",
        date: 'August 31, 2026',
        author: 'SponsorDB Team',
        readTime: '3 min read',
        tags: ['Data', 'Audience Size']
    }
];

const Blog = () => {
    const navigate = useNavigate();

    return (
        <div className="web-page blog-page">
            <Helmet>
                <title>Newsletter Sponsorship Data & Trends | SponsorDB Blog</title>
                <meta name="description" content="Data-driven reports on newsletter sponsorships — which industries are paying, what audience size sponsors actually target, and other trends pulled from SponsorDB's real, verified sponsor database." />
                <link rel="canonical" href="https://sponsor-db.com/blog" />
            </Helmet>
            <div className="blog-page__header">
                <p className="blog-page__eyebrow">Sponsorship data</p>
                <h1 className="blog-page__title">From the SponsorDB Database</h1>
                <p className="blog-page__subtitle">
                    Short, data-driven reports pulled from what we've actually tracked and verified — no generic advice, just the numbers.
                </p>
            </div>

            <div className="blog-content">
                <div className="blog-grid">
                    {posts.map((post) => (
                        <div
                            key={post.id}
                            className="blog-card"
                            onClick={() => navigate(`/blog/${post.id}`)}
                        >
                            <div className="blog-card__content">
                                <div className="blog-card__tags">
                                    {post.tags.map((tag) => (
                                        <span key={tag} className="blog-card__tag">
                                            <FontAwesomeIcon icon={tagIcons[tag] || tagIcons.default} />
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                                <h2 className="blog-card__title">{post.title}</h2>
                                <p className="blog-card__excerpt">{post.excerpt}</p>
                                <div className="blog-card__meta">
                                    <div className="blog-card__author">
                                        <FontAwesomeIcon icon={faUser} />
                                        <span>{post.author}</span>
                                    </div>
                                    <div className="blog-card__date">
                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                        <span>{post.date}</span>
                                    </div>
                                    <div className="blog-card__read-time">
                                        <FontAwesomeIcon icon={faClock} />
                                        <span>{post.readTime}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Blog;
